import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { useUsers } from '../../lib/supabase/users/supabaseUserService';
import { useBulkUserStats } from '../../lib/supabase/shifts/supabaseShiftService';
import { LoadingState } from '../ui/loading-state';
import { getUserName } from '../../utils/format';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff } from 'lucide-react';
import { DataTable } from '../DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { useRoles } from '../../lib/supabase/roles/supabaseRoleService';
import { Badge } from "../ui/badge";
import { Breadcrumbs } from '../ui/breadcrumbs';
import { getRollingWindowBoundary } from '../../lib/utils';
import { Label } from '../ui/label';

// Type definition for user stats row
type UserStatsRow = {
  user_id: string;
  userName: string;
  shiftsCount: number;
  nonPPVAverage: number;
  modelAverage: number;
  category: 'good' | 'mid' | 'bad';
  totalNonPPV: number;
  totalPPV: number;
  hoursWorked: number;
  workerType: 'weekday' | 'weekend';
  relativePerformance: number;
  relativeNonPPVPerformance: number;
  relativePPVPerformance: number;
};

const STORAGE_KEY_PREFIX = 'shiftly-averages-';

// Update performance brackets to be based on relative performance
const PERFORMANCE_BRACKETS = {
  good: 1.2,  // 20% above average
  mid: 0.8    // 20% below average
} as const;

const ShiftAveragesView = () => {
  // Initialize states with localStorage values if available
  const [payClassFilter, setPayClassFilter] = useState<string>(() => {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}pay_class`) || 'UK';
  });
  
  const [roleFilter, setRoleFilter] = useState<string>(() => {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}role`) || 'all';
  });
  
  const [showFullInfo, setShowFullInfo] = useState<boolean>(() => {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}showFullInfo`) === 'true';
  });
  
  const [hideZeroShifts, setHideZeroShifts] = useState<boolean>(() => {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}hideZeroShifts`) === 'true';
  });
  
  const [windowPeriod, setWindowPeriod] = useState<'30' | '14' | '7'>(() => {
    return (localStorage.getItem(`${STORAGE_KEY_PREFIX}windowPeriod`) as '30' | '14' | '7') || '30';
  });

  // Add category filter state
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'good' | 'mid' | 'bad'>('all');

  const { data: users = [], isLoading: usersLoading } = useUsers();
  const { data: roles = [], isLoading: rolesLoading } = useRoles();

  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}pay_class`, payClassFilter);
  }, [payClassFilter]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}role`, roleFilter);
  }, [roleFilter]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}showFullInfo`, showFullInfo.toString());
  }, [showFullInfo]);

  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}hideZeroShifts`, hideZeroShifts.toString());
  }, [hideZeroShifts]);
  
  useEffect(() => {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}windowPeriod`, windowPeriod);
  }, [windowPeriod]);

  // Get date range based on selected window period
  const dateRange = useMemo(() => {
    const days = parseInt(windowPeriod);
    return getRollingWindowBoundary(days);
  }, [windowPeriod]);

  // Get all user stats at once 
  const statsQuery = useBulkUserStats(
    dateRange.startDate,
    dateRange.endDate,
    'no-ot' // Only regular shifts, no overtime
  );

  const { 
    data: userStatsMap = {}, 
    modelAverages = {},
    isLoading: statsLoading, 
    isError: statsError 
  } = statsQuery;

  // Process user data for DataTable
  const processedData = useMemo(() => {
    if (usersLoading || statsLoading || rolesLoading || !users.length) return [];

    // Role IDs for filtering
    const chatterRoleId = roles.find(role => role.name.toLowerCase() === 'chatter')?.id;
    const trialRoleId = roles.find(role => role.name.toLowerCase() === 'trial')?.id;

    // Filter users by pay_class, active status, and role
    const filteredUsers = users.filter(user => {
      // Basic filters
      const matchesPayClass = user.pay_class === payClassFilter;
      const isActive = !user.deactivated_at && user.roles?.length > 0;
      
      // Role filtering
      let matchesRoleFilter = true;
      if (roleFilter !== 'all' && user.roles) {
        if (roleFilter === 'chatter') {
          matchesRoleFilter = chatterRoleId ? user.roles.includes(chatterRoleId) : false;
        } else if (roleFilter === 'trial') {
          matchesRoleFilter = trialRoleId ? user.roles.includes(trialRoleId) : false;
        }
      }
      
      return matchesPayClass && isActive && matchesRoleFilter;
    });

    // Map users to stats rows
    return filteredUsers.map(user => {
      const userStats = userStatsMap[user.id];
      
      // Default values in case user has no stats
      const defaultRow: UserStatsRow = {
        user_id: user.id,
        userName: getUserName(users, user.id),
        shiftsCount: 0,
        nonPPVAverage: 0,
        modelAverage: 0,
        category: 'bad',
        totalNonPPV: 0,
        totalPPV: 0,
        hoursWorked: 0,
        workerType: 'weekday',
        relativePerformance: 1,
        relativeNonPPVPerformance: 1,
        relativePPVPerformance: 1
      };
      
      // Return default row if user has no stats
      if (!userStats || userStats.overall.totalShifts === 0) {
        return defaultRow;
      }
      
      // Calculate non-PPV average based on worker type
      const nonPPVAverage = userStats.overall.workerType === 'weekend' 
        ? userStats.overall.averageNonPPVPer12HourShift 
        : userStats.overall.averageNonPPVPer8HourShift;

      // Calculate weighted model average based on hours worked on each model
      let totalModelAverage = 0;
      let totalHours = 0;
      
      Object.entries(userStats.byModel).forEach(([model_id, modelStats]) => {
        const typedModelStats = modelStats as { hoursWorked: number };
        const modelAveragesForModel = modelAverages[model_id];
        if (modelAveragesForModel && typedModelStats.hoursWorked > 0) {
          const relevantAverage = userStats.overall.workerType === 'weekend' 
            ? modelAveragesForModel.weekend.averageNonPPVPerHour * 12  // 12-hour shift equivalent
            : modelAveragesForModel.weekday.averageNonPPVPerHour * 8;   // 8-hour shift equivalent
            
          totalModelAverage += relevantAverage * typedModelStats.hoursWorked;
          totalHours += typedModelStats.hoursWorked;
        }
      });
      
      const modelAverage = totalHours > 0 ? totalModelAverage / totalHours : 0;
      
      // Determine category based on relative performance
      let category: 'good' | 'mid' | 'bad' = 'bad';
      const relativePerformance = userStats.overall.averageRelativeNonPPVPerformance;
      
      if (relativePerformance >= PERFORMANCE_BRACKETS.good) {
        category = 'good';
      } else if (relativePerformance >= PERFORMANCE_BRACKETS.mid) {
        category = 'mid';
      }
      
      return {
        user_id: user.id,
        userName: getUserName(users, user.id),
        shiftsCount: userStats.overall.totalShifts,
        nonPPVAverage,
        modelAverage,
        category,
        totalNonPPV: userStats.overall.totalNonPPV,
        totalPPV: userStats.overall.totalPPV,
        hoursWorked: userStats.overall.hoursWorked,
        workerType: userStats.overall.workerType,
        relativePerformance: userStats.overall.averageRelativePerformance,
        relativeNonPPVPerformance: userStats.overall.averageRelativeNonPPVPerformance,
        relativePPVPerformance: userStats.overall.averageRelativePPVPerformance
      };
    });
  }, [users, userStatsMap, modelAverages, payClassFilter, roleFilter, usersLoading, statsLoading, rolesLoading, roles]);

  // Stats summary for quick overview (calculated from unfiltered data)
  const statsSummary = useMemo(() => {
    if (!processedData.length) return { total: 0, good: 0, mid: 0, bad: 0, noShifts: 0 };
    
    const total = processedData.length;
    const good = processedData.filter(row => row.category === 'good' && row.shiftsCount > 0).length;
    const mid = processedData.filter(row => row.category === 'mid' && row.shiftsCount > 0).length;
    const bad = processedData.filter(row => row.category === 'bad' && row.shiftsCount > 0).length;
    const noShifts = processedData.filter(row => row.shiftsCount === 0).length;
    
    return { total, good, mid, bad, noShifts };
  }, [processedData]);

  // Filter data for table display
  const tableData = useMemo(() => {
    let filteredData = [...processedData];

    // Filter out users with zero shifts if hideZeroShifts is enabled
    if (hideZeroShifts) {
      filteredData = filteredData.filter(row => row.shiftsCount > 0);
    }

    // Apply category filter if not 'all'
    if (categoryFilter !== 'all') {
      filteredData = filteredData.filter(row => row.category === categoryFilter && row.shiftsCount > 0);
    }
    
    return filteredData;
  }, [processedData, hideZeroShifts, categoryFilter]);

  // Define columns for DataTable - streamlined version
  const columns = useMemo<ColumnDef<UserStatsRow>[]>(() => [
    {
      accessorKey: 'userName',
      header: 'User',
      cell: ({ row }: { row: any }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.getValue('userName')}</span>
          <Badge variant="outline" className={`text-xs ${
            row.original.workerType === 'weekend' 
              ? 'bg-brand-pink text-brand-dark dark:bg-brand-dark/30 dark:text-brand-pink border-brand-pink dark:border-brand-border'
              : 'bg-brand-gold text-brand-dark dark:bg-brand-dark/30 dark:text-brand-gold border-brand-gold dark:border-brand-border'
          }`}>
            {row.original.workerType === 'weekend' ? 'Weekend' : 'Weekday'}
          </Badge>
        </div>
      ),
    },
    {
      accessorKey: 'shiftsCount',
      header: 'Shifts',
      cell: ({ row }: { row: any }) => row.getValue('shiftsCount'),
    },
    {
      accessorKey: 'nonPPVComparison',
      header: 'Non-PPV Performance',
      accessorFn: (row) => {
        return row.relativeNonPPVPerformance;
      },
      cell: ({ row }: { row: any }) => {
        const userAvg = row.original.nonPPVAverage as number;
        const modelAvg = row.original.modelAverage as number;
        const relPerformance = row.original.relativeNonPPVPerformance as number;
        const percentage = relPerformance ? ((relPerformance - 1) * 100).toFixed(1) : '0.0';
        const isPositive = relPerformance >= 1;
        
        if (userAvg === 0 || modelAvg === 0) {
          return <div className="text-muted-foreground">No data</div>;
        }
        
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium">${userAvg.toFixed(2)}</div>
              <div className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                isPositive ? 'text-brand-dark bg-brand-green dark:text-brand-green dark:bg-brand-dark/30' 
                          : 'text-brand-red bg-brand-red dark:text-brand-red dark:bg-brand-dark/30'
              }`}>
                {isPositive ? '+' : ''}{percentage}%
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              vs. model avg ${modelAvg.toFixed(2)}
            </div>
          </div>
        );
      },
    },
    ...(showFullInfo ? [
      {
        accessorKey: 'earningsBreakdown',
        header: 'Earnings Breakdown',
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const totalPPV = row.original.totalPPV as number;
          const totalNonPPV = row.original.totalNonPPV as number;
          const hoursWorked = row.original.hoursWorked as number;
          const total = totalPPV;
          
          const nonPPVPercentage = total > 0 ? (totalNonPPV / total) * 100 : 0;
          
          return (
            <div className="flex flex-col gap-1">
              <div className="flex justify-between text-xs">
                <span>Total: ${total.toFixed(2)}</span>
                <span>{hoursWorked.toFixed(1)} hrs</span>
              </div>
              <div className="w-full h-2 bg-brand-dark dark:bg-brand-dark rounded-full overflow-hidden">
                <div 
                  className="h-full bg-brand-green dark:bg-brand-green" 
                  style={{ width: `${nonPPVPercentage}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Non-PPV: ${totalNonPPV.toFixed(2)} ({nonPPVPercentage.toFixed(0)}%)</span>
                <span>PPV Total: ${totalPPV.toFixed(2)}</span>
              </div>
            </div>
          );
        }
      }
    ] : []),
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }: { row: any }) => {
        const category = row.getValue('category') as 'good' | 'mid' | 'bad';
        const shiftsCount = row.getValue('shiftsCount') as number;
        
        let categoryColor = 'text-destructive bg-destructive/10';
        let categoryLabel = 'BELOW AVG';
        
        if (category === 'good') {
          categoryColor = 'text-brand-green bg-brand-green dark:text-brand-green dark:bg-brand-dark/30';
          categoryLabel = 'ABOVE AVG';
        } else if (category === 'mid') {
          categoryColor = 'text-amber-600 bg-amber-50 dark:text-amber-400 dark:bg-amber-950/30';
          categoryLabel = 'AVERAGE';
        }
        
        if (shiftsCount === 0) {
          return <div className="text-muted-foreground">No shifts</div>;
        }
        
        return (
          <span className={`px-2 py-1 rounded-md text-xs font-medium ${categoryColor}`}>
            {categoryLabel}
          </span>
        );
      },
    }
  ], [showFullInfo]);

  if (usersLoading || statsLoading || rolesLoading) {
    return (
      <div className="py-8">
        <LoadingState />
        <p className="text-center text-muted-foreground mt-4">
          Loading user statistics{statsLoading ? " - this may take a moment..." : ""}
        </p>
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="py-8">
        <Alert variant="destructive">
          <AlertDescription>
            There was an error loading shift statistics. Please try again later.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const breadcrumbs = [
    { title: 'Home', href: '/' },
    { title: 'Sales Rota', href: '/rota' },
    { title: 'Shift Averages', href: '/shift-averages' }
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Breadcrumbs items={breadcrumbs} className="mb-2" />
        <h1 className="text-3xl font-bold tracking-tight text-brand-dark dark:text-foreground">
          Shift Averages
        </h1>
        <p className="text-muted-foreground dark:text-muted-foreground">
          View how users are performing relative to model averages over the last {windowPeriod} days.
        </p>
      </div>

      <Card>
        <div className="p-6 border-b border-brand-border dark:border-brand-border">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3 justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-medium">Performance Overview - Last {windowPeriod} Days</h2>
              </div>
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-semibold">{statsSummary.total}</span>
                  <span className="text-muted-foreground">Total Users</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div 
                className={`flex flex-col justify-between p-4 rounded-lg border border-brand-green dark:border-brand-border/50 bg-brand-green dark:bg-brand-dark/20 cursor-pointer transition-colors ${
                  categoryFilter === 'good' ? 'ring-2 ring-green-500 dark:ring-green-400' : ''
                }`}
                onClick={() => setCategoryFilter(categoryFilter === 'good' ? 'all' : 'good')}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-sm font-medium text-brand-green dark:text-brand-green">Above Average</p>
                  <p className="text-sm font-medium text-brand-dark dark:text-brand-green">
                    +{((PERFORMANCE_BRACKETS.good - 1) * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-semibold text-brand-dark dark:text-brand-green">
                      {statsSummary.good}
                    </span>
                    <span className="text-lg font-medium text-brand-green dark:text-brand-green">
                      {statsSummary.total > statsSummary.noShifts
                        ? ((statsSummary.good / (statsSummary.total - statsSummary.noShifts)) * 100).toFixed(1)
                        : "0.0"}%
                    </span>
                  </div>
                  <div className="w-full bg-brand-green dark:bg-brand-dark/40 rounded-full h-2">
                    <div 
                      className="bg-brand-green dark:bg-brand-green h-2 rounded-full" 
                      style={{ 
                        width: statsSummary.total > statsSummary.noShifts
                          ? `${(statsSummary.good / (statsSummary.total - statsSummary.noShifts)) * 100}%`
                          : "0%"
                      }} 
                    />
                  </div>
                  <span className="text-sm text-brand-green dark:text-brand-green">Users</span>
                </div>
              </div>

              <div 
                className={`flex flex-col justify-between p-4 rounded-lg border border-amber-100 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 cursor-pointer transition-colors ${
                  categoryFilter === 'mid' ? 'ring-2 ring-amber-500 dark:ring-amber-400' : ''
                }`}
                onClick={() => setCategoryFilter(categoryFilter === 'mid' ? 'all' : 'mid')}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Average Performance</p>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    {((PERFORMANCE_BRACKETS.mid - 1) * 100).toFixed(0)}% to +{((PERFORMANCE_BRACKETS.good - 1) * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-semibold text-amber-700 dark:text-amber-300">
                      {statsSummary.mid}
                    </span>
                    <span className="text-lg font-medium text-amber-600 dark:text-amber-400">
                      {statsSummary.total > statsSummary.noShifts
                        ? ((statsSummary.mid / (statsSummary.total - statsSummary.noShifts)) * 100).toFixed(1)
                        : "0.0"}%
                    </span>
                  </div>
                  <div className="w-full bg-amber-200 dark:bg-amber-900/40 rounded-full h-2">
                    <div 
                      className="bg-amber-600 dark:bg-amber-400 h-2 rounded-full" 
                      style={{ 
                        width: statsSummary.total > statsSummary.noShifts
                          ? `${(statsSummary.mid / (statsSummary.total - statsSummary.noShifts)) * 100}%`
                          : "0%"
                      }} 
                    />
                  </div>
                  <span className="text-sm text-amber-600 dark:text-amber-400">Users</span>
                </div>
              </div>

              <div 
                className={`flex flex-col justify-between p-4 rounded-lg border border-brand-red dark:border-brand-border/50 bg-brand-red dark:bg-brand-dark/20 cursor-pointer transition-colors ${
                  categoryFilter === 'bad' ? 'ring-2 ring-red-500 dark:ring-red-400' : ''
                }`}
                onClick={() => setCategoryFilter(categoryFilter === 'bad' ? 'all' : 'bad')}
                role="button"
                tabIndex={0}
              >
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-sm font-medium text-brand-red dark:text-brand-red">Below Average</p>
                  <p className="text-sm font-medium text-brand-red dark:text-brand-red">
                    Below {((PERFORMANCE_BRACKETS.mid - 1) * 100).toFixed(0)}%
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between">
                    <span className="text-2xl font-semibold text-brand-red dark:text-brand-red">
                      {statsSummary.bad}
                    </span>
                    <span className="text-lg font-medium text-brand-red dark:text-brand-red">
                      {statsSummary.total > statsSummary.noShifts
                        ? ((statsSummary.bad / (statsSummary.total - statsSummary.noShifts)) * 100).toFixed(1)
                        : "0.0"}%
                    </span>
                  </div>
                  <div className="w-full bg-brand-red dark:bg-brand-dark/40 rounded-full h-2">
                    <div 
                      className="bg-brand-red dark:bg-brand-red h-2 rounded-full" 
                      style={{ 
                        width: statsSummary.total > statsSummary.noShifts
                          ? `${(statsSummary.bad / (statsSummary.total - statsSummary.noShifts)) * 100}%`
                          : "0%"
                      }} 
                    />
                  </div>
                  <span className="text-sm text-brand-red dark:text-brand-red">Users</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-b border-brand-border dark:border-brand-border bg-brand-dark dark:bg-brand-dark/50">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <Label className="text-sm font-medium mb-2.5 block text-muted-foreground">Pay Class</Label>
              <ToggleGroup type="single" value={payClassFilter} onValueChange={(value) => value && setPayClassFilter(value)}>
                <ToggleGroupItem value="UK">UK Users</ToggleGroupItem>
                <ToggleGroupItem value="FP">FP Users</ToggleGroupItem>
              </ToggleGroup>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2.5 block text-muted-foreground">Role</Label>
              <ToggleGroup type="single" value={roleFilter} onValueChange={(value) => value && setRoleFilter(value)}>
                <ToggleGroupItem value="all">All Roles</ToggleGroupItem>
                <ToggleGroupItem value="chatter">Chatters</ToggleGroupItem>
                <ToggleGroupItem value="trial">Trial</ToggleGroupItem>
              </ToggleGroup>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2.5 block text-muted-foreground">Time Period</Label>
              <ToggleGroup type="single" value={windowPeriod} onValueChange={(value) => value && setWindowPeriod(value as '30' | '14' | '7')}>
                <ToggleGroupItem value="30">30 Days</ToggleGroupItem>
                <ToggleGroupItem value="14">14 Days</ToggleGroupItem>
                <ToggleGroupItem value="7">7 Days</ToggleGroupItem>
              </ToggleGroup>
            </div>

            <div className="ml-auto">
              <Label className="text-sm font-medium mb-2.5 block text-muted-foreground">Options</Label>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-white dark:bg-brand-dark rounded-md border px-3 py-2">
                  <Checkbox 
                    id="filter-zero-shifts" 
                    checked={hideZeroShifts} 
                    onCheckedChange={(checked) => setHideZeroShifts(checked === true)}
                  />
                  <Label 
                    htmlFor="filter-zero-shifts" 
                    className="text-sm font-medium cursor-pointer whitespace-nowrap"
                  >
                    Hide users with no shifts
                  </Label>
                </div>
                <div className="flex items-center gap-2 bg-white dark:bg-brand-dark rounded-md border px-3 py-2">
                  <Button 
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFullInfo(!showFullInfo)}
                    className="h-auto p-0"
                  >
                    {showFullInfo ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                    {showFullInfo ? "Hide Earnings Details" : "Show Earnings Details"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <CardContent className="pt-6">
          <DataTable 
            data={tableData} 
            columns={columns}
            initialSorting={[{ id: 'nonPPVComparison', desc: true }]}
            pageSize={20}
            searchPlaceholder="Search users..."
            tableId={`shift-averages-${payClassFilter.toLowerCase()}-${roleFilter}`}
            getRowUrl={(row) => `/users/${row.user_id}`}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftAveragesView; 